import { Timestamp, DocumentData } from 'firebase/firestore';
import type { FinancialRecord, MonthlyData, FinanceError } from '../../types/finance';
import { CAMPOS_FIJOS_GUARDADOS, CAMPOS_VARIABLES_GUARDADOS } from './partidas';

export const formatFinanceError = (error: FinanceError): string => {
    switch (error.type) {
        case 'PERMISSION_DENIED':
            return `Permiso denegado para franquicia: ${error.franchiseId}`;
        case 'VALIDATION_ERROR':
            return `Error de validación en ${error.field}: ${error.message}`;
        case 'NOT_FOUND':
            return `Recurso no encontrado (ID: ${error.franchiseId})`;
        case 'NETWORK_ERROR':
            return `Error de red: ${error.cause ? (error.cause as Error).message : 'Desconocido'}`;
        case 'INVALID_FORMAT':
            return `Formato inválido en ${error.field}. Esperado: ${error.expected}, Recibido: ${error.received}`;
        case 'UNKNOWN_ERROR':
            return `Error desconocido: ${error.message}`;
        default:
            return 'Ha ocurrido un error inesperado.';
    }
};

// Normalize dates from various formats (Date, string, Timestamp, Firebase Timestamp)
const normalizeDate = (date: Date | string | Timestamp | { toDate: () => Date }): Date => {
    if (date instanceof Date) return date;
    if (typeof date === 'string') return new Date(date);
    if ('toDate' in date) return date.toDate();
    return new Date();
};

// Normalize timestamps (ServerTimestamp or Firebase Timestamp)
const normalizeTimestamp = (ts: unknown): Date | null => {
    if (!ts) return null;
    if (ts instanceof Date) return ts;
    if (typeof ts === 'string') return new Date(ts);
    if (ts !== null && typeof ts === 'object' && 'toDate' in ts) return (ts as { toDate: () => Date }).toDate();
    return null;
};

// Map Firestore data to FinancialRecord (handle legacy field names)
export const mapToFinancialRecord = (docSnap: { id: string; data: () => DocumentData }): FinancialRecord => {
    const data = docSnap.data();
    return {
        id: docSnap.id,
        franchiseId: data.franchiseId || data.franchise_id || '',
        amount: Number(data.amount) || 0,
        date: normalizeDate(data.date),
        status: data.status,
        type: data.type,
        category: data.category || '',
        description: data.description || '',
        adminNotes: data.adminNotes || data.admin_notes || '',
        revenue: data.revenue,
        expenses: data.expenses,
        profit: data.profit,
        logisticsIncome: data.logisticsIncome,
        breakdown: data.breakdown,
        createdAt: normalizeTimestamp(data.createdAt || data.created_at) || undefined,
        updatedAt: normalizeTimestamp(data.updatedAt || data.updated_at) || undefined,
        submittedAt: normalizeTimestamp(data.submittedAt || data.submitted_at) || undefined,
        approvedAt: normalizeTimestamp(data.approvedAt || data.approved_at) || undefined,
        approvedBy: data.approvedBy || data.approved_by,
        rejectionReason: data.rejectionReason || data.rejection_reason,
        isLocked: data.isLocked ?? data.is_locked ?? false
    };
};

// Map Firestore data to MonthlyData (handle legacy field names)
export const mapToMonthlyData = (docSnap: { id: string; data: () => DocumentData }, franchiseId?: string): MonthlyData => {
    const data = docSnap.data();
    const revenue = Number(data.totalIncome || data.revenue || 0);
    const expenses = Number(data.totalExpenses || data.expenses || 0);

    return {
        ...data,
        id: docSnap.id,
        revenue,
        expenses,
        profit: revenue - expenses,
        totalIncome: revenue,
        totalExpenses: expenses,
        isLocked: data.isLocked ?? data.is_locked ?? false,
        franchiseId: data.franchiseId || data.franchise_id || franchiseId
    };
};

// Safe number conversion
export const getSafeNumber = (val: unknown): number => Number(val) || 0;

/**
 * Precedencia canónica de campos de un resumen mensual.
 * Todos los consumidores (admin, franquicia, tendencias) deben usar estos
 * helpers en vez de reimplementar `totalIncome || revenue` con órdenes distintos.
 * Orden documentado: totalIncome → revenue → summary.grossIncome → grossIncome.
 */
export const getSummaryRevenue = (summary: DocumentData | Record<string, unknown>): number => {
    const nested = summary.summary as Record<string, unknown> | undefined;
    // `||` intencionado: en datos legacy un 0 en el campo preferente convive con
    // el valor real en un alias — igual que hace mapToMonthlyData.
    return getSafeNumber(summary.totalIncome) || getSafeNumber(summary.revenue) ||
        getSafeNumber(nested?.grossIncome) || getSafeNumber(summary.grossIncome);
};

/**
 * Partidas de gasto que el franquiciado registra una a una en el cierre.
 * El orden y la composición replican `calculateExpenses` de `lib/finance.ts`:
 * si aquí faltara una, el resumen mensual y el panel dirían cifras distintas.
 */
// La lista vive en `partidas.ts`: antes estaba escrita aqui, otra vez en
// `financeCalculations.ts` y una tercera en `lib/finance.ts`, y añadir un campo
// obligaba a acordarse de los tres.
const PARTIDAS = [...CAMPOS_FIJOS_GUARDADOS, ...CAMPOS_VARIABLES_GUARDADOS];

/**
 * Suma de lo que el usuario registró de verdad, partida a partida.
 *
 * Devuelve `null` cuando el resumen no tiene ninguna partida: eso distingue
 * "no registró gastos" de "registró gastos que suman cero", y permite que los
 * documentos antiguos —agregados sin desglose— sigan usando su total guardado.
 */
export const sumSummaryExpenseConcepts = (
    summary: DocumentData | Record<string, unknown>
): number | null => {
    const presentes = PARTIDAS.filter((k) => summary[k] !== undefined && summary[k] !== null);

    const rentingCost = getSafeNumber(summary.rentingCost);
    const tieneRenting = summary.rentingCost !== undefined && summary.rentingCost !== null;

    const esSuscripcion = summary.pricingModel === 'subscription';
    const suscripcion = esSuscripcion ? getSafeNumber(summary.platformSubscription) : 0;
    const tieneSuscripcion = esSuscripcion && summary.platformSubscription !== undefined;

    // El royalty no se almacena: es un porcentaje sobre lo facturado.
    const royalty = esSuscripcion
        ? 0
        : getSummaryRevenue(summary) * (getSafeNumber(summary.royaltyPercent) / 100);

    if (presentes.length === 0 && !tieneRenting && !tieneSuscripcion) return null;

    return PARTIDAS.reduce((s, k) => s + getSafeNumber(summary[k]), 0)
        + rentingCost + suscripcion + royalty;
};

/**
 * Gasto del mes.
 *
 * Manda la suma de las partidas registradas. El total agregado solo se usa
 * cuando no hay desglose (documentos antiguos), porque puede quedarse obsoleto:
 * en producción había meses cuyo `totalExpenses` guardado excluía nóminas y
 * renting, de modo que el panel decía 812,28 € y el informe anual 225,64 €.
 */
export const getSummaryExpenses = (summary: DocumentData | Record<string, unknown>): number => {
    const registrado = sumSummaryExpenseConcepts(summary);
    if (registrado !== null) return registrado;

    const nested = summary.summary as Record<string, unknown> | undefined;
    return getSafeNumber(summary.totalExpenses) || getSafeNumber(summary.expenses) ||
        getSafeNumber(nested?.totalExpenses);
};

/** Beneficio pre-impuestos derivado — el campo `profit` almacenado puede ser legacy post-IRPF. */
export const getSummaryProfit = (summary: DocumentData | Record<string, unknown>): number =>
    getSummaryRevenue(summary) - getSummaryExpenses(summary);

/**
 * Estado de completitud de un mes.
 *
 * Un mes con facturación cargada y gastos sin revisar no tiene resultado: si se
 * presenta `ingresos - 0` como beneficio, la flota ve su mejor mes del año donde
 * en realidad solo falta trabajo por hacer. `expensesConfirmed` es la vía para
 * declarar explícitamente que ese mes no tuvo gastos.
 */
export type MonthCompleteness = 'sin-datos' | 'pendiente-de-gastos' | 'completo';

export const getMonthCompleteness = (
    summary: DocumentData | Record<string, unknown>
): MonthCompleteness => {
    const revenue = getSummaryRevenue(summary);
    const expenses = getSummaryExpenses(summary);

    if (revenue === 0 && expenses === 0) return 'sin-datos';
    if (expenses === 0 && summary.expensesConfirmed !== true) return 'pendiente-de-gastos';
    return 'completo';
};

/** `true` solo si el mes tiene ingresos y gastos revisados. */
export const hasReliableResult = (summary: DocumentData | Record<string, unknown>): boolean =>
    getMonthCompleteness(summary) === 'completo';

/** Resultado del mes, o `null` si todavía no se puede afirmar. */
export const getReliableProfit = (
    summary: DocumentData | Record<string, unknown>
): number | null => (hasReliableResult(summary) ? getSummaryProfit(summary) : null);

// Generate month key from Date or string (YYYY-MM format)
export const generateMonthKey = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};